import React, { useState, useEffect } from 'react';
import { Check, Crosshair, Info } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCockpitExplorer } from '../hooks/useCockpitExplorer';
import { useAssistancePanel } from '../contexts/AssistancePanelContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { matchChecklistLocate } from '../utils/cockpitMatch';

const PLACEHOLDER_HINTS = {
  '[CLEARANCE_XPDR]': 'Squawk from your clearance',
  '[ASSIGNED_RUNWAY]': 'Runway from ATC',
};

const ChecklistItem = (props) => {
  const isPlaceholder = props.action.startsWith('[') && props.action.endsWith(']');
  const hint = PLACEHOLDER_HINTS[props.action] || 'From ATC / briefing';

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      props.onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={props.onToggle}
      onKeyDown={handleKeyDown}
      aria-pressed={props.ticked}
      className="w-full flex items-center flex-wrap gap-x-3.5 gap-y-1 min-h-[48px] md:min-h-[52px] py-2 px-4 bg-transparent border-0 border-b cursor-pointer text-left transition-colors hover:bg-glass-card/40"
      style={{ borderColor: props.isNoteExpanded ? 'transparent' : 'var(--gc-border-row)' }}
      data-testid={`checklist-item-${props.index}`}
    >
      {/* Tick box */}
      {props.ticked ? (
        <span
          className="w-6 h-6 flex-shrink-0 rounded-[5px] flex items-center justify-center"
          style={{ background: 'var(--gc-green)' }}
          data-testid={`tick-box-${props.index}`}
        >
          <Check className="w-3.5 h-3.5" style={{ color: 'var(--gc-green-text-on)' }} strokeWidth={3.2} />
        </span>
      ) : (
        <span
          className="w-6 h-6 flex-shrink-0 rounded-[5px] box-border"
          style={{ border: '1.5px solid var(--gc-cyan)' }}
          data-testid={`tick-box-${props.index}`}
        />
      )}

      {/* Item text - wraps rather than clipping on narrow columns */}
      <span
        className="font-plex-mono text-[14px] font-medium tracking-[0.02em] break-words min-w-[90px] flex-1 basis-[140px]"
        style={{ color: props.ticked ? 'var(--gc-text-ticked)' : 'var(--gc-text-main)' }}
      >
        {props.item}
      </span>

      {/* Dotted leader - shrinks away first, never forces overflow */}
      <span
        className="min-w-0 h-0 mt-[9px] self-start hidden sm:block"
        style={{ flex: '0 1 12px', borderBottom: '1.5px dotted var(--gc-border-dotted)' }}
      />

      {/* Info icon - expands/collapses this item's note, never ticks the row */}
      {props.note && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            props.onToggleNote();
          }}
          className="shrink-0 flex items-center justify-center rounded-full cursor-pointer"
          style={{ width: '32px', height: '32px', color: 'var(--gc-cyan)' }}
          aria-label={`${props.isNoteExpanded ? 'Hide' : 'Show'} note for ${props.item}`}
          aria-expanded={props.isNoteExpanded}
          data-testid={`checklist-note-toggle-${props.index}`}
        >
          <Info className="w-4 h-4" strokeWidth={2} />
        </button>
      )}

      {/* Locate icon - find this item on the cockpit, never ticks the row */}
      {props.locate && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            props.onLocate();
          }}
          className="shrink-0 flex items-center justify-center rounded-full cursor-pointer"
          style={{ width: '32px', height: '32px', color: 'var(--gc-cyan)' }}
          aria-label={`Find ${props.item} on the cockpit`}
          data-testid={`checklist-locate-${props.index}`}
        >
          <Crosshair className="w-4 h-4" strokeWidth={2} />
        </button>
      )}

      {/* Action */}
      {isPlaceholder ? (
        <span className="shrink-0 text-sm font-plex-mono px-2 py-0.5 rounded-md bg-glass-amber/15 text-glass-amber border border-glass-amber-border break-words text-right max-w-full">
          {hint}
        </span>
      ) : (
        <span
          className="shrink-0 font-plex-mono text-[14px] font-semibold tracking-[0.02em] text-right break-words max-w-full"
          style={{ color: props.ticked ? 'var(--gc-green)' : 'var(--gc-cyan)' }}
        >
          {props.action}
        </span>
      )}
    </div>
  );
};

const ChecklistPhase = (props) => {
  const phase = props.phase;
  const { data: explorerData } = useCockpitExplorer();
  const { openLocate } = useAssistancePanel();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedNoteIndex, setExpandedNoteIndex] = useState(null);

  // All notes start collapsed every time a phase opens.
  useEffect(() => {
    setExpandedNoteIndex(null);
  }, [phase?.id]);

  if (!phase || !phase.checklist_items) return null;

  const ticked = phase.checklist_items.filter(i => i.ticked).length;
  const total = phase.checklist_items.length;
  const isComplete = ticked >= total && total > 0;

  const handleToggleNote = (orderIndex) => {
    setExpandedNoteIndex((prev) => (prev === orderIndex ? null : orderIndex));
  };

  const handleLocate = (match) => {
    if (isMobile) {
      if (match.type === 'single') {
        navigate(`/cockpit/${match.zoneId}?view=${match.viewId}&control=${match.control.id}`, {
          state: { returnTo: location.pathname },
        });
      } else {
        const first = match.steps[0];
        navigate(`/cockpit/${first.zoneId}?view=${first.viewId}&control=${first.control.id}`, {
          state: { returnTo: location.pathname, steps: match.steps, itemText: match.itemText, phaseTitle: match.phaseTitle },
        });
      }
    } else {
      openLocate(match);
    }
  };

  return (
    <div data-testid="checklist-phase">
      {/* Checklist rows */}
      <div
        className="rounded-gc-checklist overflow-hidden bg-glass-checklist-card border"
        style={{ borderColor: 'var(--gc-border-card)' }}
        data-testid="checklist-rows-container"
      >
        {phase.checklist_items.map((item) => {
          const match = explorerData
            ? matchChecklistLocate(item.item, item.action, phase.title, explorerData)
            : null;
          const locatePayload = match
            ? { ...match, itemText: item.item, phaseTitle: phase.title }
            : null;
          const isNoteExpanded = expandedNoteIndex === item.order_index;

          return (
            <React.Fragment key={`${item.item}-${item.order_index}`}>
              <ChecklistItem
                item={item.item}
                action={item.action}
                ticked={item.ticked}
                index={item.order_index}
                note={item.note}
                isNoteExpanded={isNoteExpanded}
                onToggleNote={() => handleToggleNote(item.order_index)}
                onToggle={() => props.onToggleTick(item.order_index, !item.ticked)}
                locate={locatePayload}
                onLocate={() => handleLocate(locatePayload)}
              />
              {isNoteExpanded && item.note && (
                <div
                  className="w-full px-4 pb-2.5 pt-0 bg-transparent border-0 border-b"
                  style={{ borderColor: 'var(--gc-border-row)' }}
                  data-testid={`checklist-note-${item.order_index}`}
                >
                  <p
                    className="font-plex-mono text-[12px] leading-snug pl-9 pr-2 break-words"
                    style={{ color: 'var(--gc-text-muted)' }}
                  >
                    {item.note}
                  </p>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isComplete && (
        <div className="flex justify-end mt-3">
          <span
            className="font-plex-mono text-xs font-semibold px-2.5 py-1 rounded-gc-badge"
            style={{ color: 'var(--gc-green)', background: 'rgba(74,222,128,0.1)' }}
            data-testid="phase-complete-badge"
          >
            PHASE COMPLETE
          </span>
        </div>
      )}
    </div>
  );
};

export default ChecklistPhase;
